/**
 * VocabularyListEditor — repeatable list of {term, meaning} pairs.
 *
 * TenantProfile `vocabulary` field: each entry is a tenant-specific
 * term paired with its meaning, used to prime agents that parse
 * tenant-authored content.
 *
 * Phase 44.4.
 */

import { useCallback } from "react";

export default function VocabularyListEditor({ items = [], onChange }) {
  const update = useCallback(
    (index, key, value) => {
      const next = items.slice();
      next[index] = { ...(next[index] || { term: "", meaning: "" }), [key]: value };
      onChange(next);
    },
    [items, onChange]
  );

  const remove = useCallback(
    (index) => {
      const next = items.slice();
      next.splice(index, 1);
      onChange(next);
    },
    [items, onChange]
  );

  const add = useCallback(() => {
    onChange([...items, { term: "", meaning: "" }]);
  }, [items, onChange]);

  return (
    <div className="vocabulary-list">
      <label className="vocabulary-list__label">Vocabulary</label>
      <p className="vocabulary-list__help">
        Terms specific to your product — used to prime agents that parse
        your documents. Define only terms that appear in your own content.
      </p>
      {items.length === 0 && (
        <p className="vocabulary-list__empty">No vocabulary entries yet.</p>
      )}
      {items.map((entry, i) => (
        <div key={i} className="vocabulary-list__row">
          <input
            type="text"
            className="vocabulary-list__term"
            value={entry?.term || ""}
            onChange={(e) => update(i, "term", e.target.value)}
            placeholder="Term"
            maxLength={100}
            aria-label={`Term ${i + 1}`}
          />
          <input
            type="text"
            className="vocabulary-list__meaning"
            value={entry?.meaning || ""}
            onChange={(e) => update(i, "meaning", e.target.value)}
            placeholder="Meaning"
            maxLength={500}
            aria-label={`Meaning ${i + 1}`}
          />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => remove(i)}
            aria-label={`Remove vocabulary entry ${i + 1}`}
          >
            ×
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-secondary btn-sm" onClick={add}>
        + Add vocabulary entry
      </button>
    </div>
  );
}
